<script setup lang="ts">
  import { ref, watch, onBeforeMount, provide, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { CheckCircleIcon } from '@heroicons/vue/24/outline'
  import {
    ChevronDown,
    ChevronUp,
    Mail,
    Copy,
    Bell,
    Settings,
    Monitor,
    AtSign,
    FileText,
    Server,
    Phone,
    BotIcon,
    GlobeIcon,
    MessageCircleIcon,
    WorkflowIcon,
    FileQuestion,
    FilesIcon,
    Palette,
    FileSignature,
    BracketsIcon,
    RefreshCcw,
    ScanSearchIcon,
    Users,
    Building2,
    CalendarCheck,
    ExternalLink,
    Package,
    UserPlus,
    PlugZap,
    Webhook,
    ShieldAlert,
    Link
  } from 'lucide-vue-next'
  import { SETTING_PAGES, SETTING_TOPICS } from '@/constants/settings/topics'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import type { CustomerUser as User } from '@/types/generated'
  import { FlagEnum, PhonebotSelfServicePlanEnum, Role5ccEnum } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useTranslation } from '@/composables/useTranslation'
  const router = useRouter()
  const route = useRoute()
  const fetchUser = useUserStore().fetchUser
  const user = computed<User>(() => useUserStore().user as User)

  // Feature flags
  const agenticWorkflowsFlag = useFeatureFlag(FlagEnum.AGENTIC_WORKFLOWS)

  // Feature flag for questionnaire self service
  const questionnaireSelfServiceFlag = useFeatureFlag(FlagEnum.QUESTIONNAIRE_SELF_SERVICE)

  const clientOnboardingFlag = useFeatureFlag(FlagEnum.CLIENT_ONBOARDING)
  // Feature flags for entity extraction
  const preflightDocumentExtractionAgentFlag = useFeatureFlag(
    FlagEnum.PREFLIGHT_DOCUMENT_EXTRACTION_AGENT
  )
  const incomingEmailEntityExtractionAgentFlag = useFeatureFlag(
    FlagEnum.INCOMING_EMAIL_ENTITY_EXTRACTION_AGENT
  )

  // Feature flag for PDF form templates
  const pdfFormTemplatesFlag = useFeatureFlag(FlagEnum.PDF_FORM_TEMPLATES)

  // Feature flag for admin-set integrations
  const adminSetIntegrationsFlag = useFeatureFlag(FlagEnum.ADMIN_SET_INTEGRATIONS)

  const { t } = useTranslation({
    en: {
      settings: 'Settings',
      emails: 'E-Mails',
      artificialIntelligence: 'Artificial Intelligence',
      aiPhoneAnswering: 'AI Phone Answering',
      aiDocumentTemplates: 'AI Document Templates',
      lawOfficeSoftware: 'Law Office Software',
      jupusEmailAddress: 'JUPUS E-Mail Address',
      templates: 'Templates',
      design: 'Design',
      signature: 'Signature',
      placeholders: 'Placeholders',
      notifications: 'Notifications',
      deliveryStatus: 'Delivery Status',
      basicSettings: 'Basic Settings',
      documentTemplates: 'Document Templates',
      promptTemplates: 'Prompt Templates',
      entityExtraction: 'Entity Extraction',
      entityExtractionInstructions: 'Instructions',
      aiWorkflows: 'AI Workflows',
      aiAutonomy: 'AI Autonomy',
      phoneNumbers: 'Phone Numbers',
      callForwardingRules: 'Call Forwarding Rules',
      questionnaires: 'Questionnaires',
      onboardingPackages: 'On Site Client Intakes',
      clientOnboarding: 'Client onboarding',
      pdfForms: 'PDF Forms',
      pdfFormTemplates: 'PDF Form Templates',
      testPhase: 'Test',
      pro: 'PRO',
      users: 'Users',
      lawFirm: 'Law Firm',
      appointmentBooking: 'Appointment Booking',
      new: 'New',
      integrations: 'Integrations',
      webhooks: 'Webhooks',
      emailEgress: 'Email Egress',
      hubspot: 'HubSpot',
      hibp: 'Have I Been Pwned',
      iusta: 'Iusta'
    },
    de: {
      settings: 'Einstellungen',
      emails: 'E-Mails',
      artificialIntelligence: 'Künstliche Intelligenz',
      aiPhoneAnswering: 'KI-Anrufbeantworter',
      aiDocumentTemplates: 'KI-Dokumentenvorlagen',
      lawOfficeSoftware: 'Kanzleisoftware',
      jupusEmailAddress: 'JUPUS E-Mail Adresse',
      templates: 'Vorlagen',
      design: 'Design',
      signature: 'Signatur',
      placeholders: 'Platzhalter',
      notifications: 'Benachrichtigungen',
      deliveryStatus: 'Versandstatus',
      basicSettings: 'Grundeinstellungen',
      documentTemplates: 'Dokumentenvorlagen',
      promptTemplates: 'Promptvorlagen',
      entityExtraction: 'Beteiligtenextraktion',
      entityExtractionInstructions: 'Anweisungen',
      aiWorkflows: 'KI Workflows',
      aiAutonomy: 'KI Autonomie',
      phoneNumbers: 'Telefonnummern',
      callForwardingRules: 'Anrufweiterleitungsregeln',
      questionnaires: 'Fragebögen',
      onboardingPackages: 'Vor-Ort Mandantenaufnahme',
      clientOnboarding: 'Mandantenaufnahme',
      pdfForms: 'PDF Formulare',
      pdfFormTemplates: 'PDF Formularvorlagen ',
      testPhase: 'Test',
      pro: 'PRO',
      new: 'Neu',
      users: 'Benutzer',
      lawFirm: 'Kanzlei',
      appointmentBooking: 'Terminbuchung',
      integrations: 'Integrationen',
      webhooks: 'Webhooks',
      emailEgress: 'E-Mail Egress',
      hubspot: 'HubSpot',
      hibp: 'Have I Been Pwned',
      iusta: 'Iusta'
    }
  })

  export interface Page {
    name: string
    page: string
  }

  export interface Setting {
    name: string
    icon: unknown
    pages: Page[]
    beta?: boolean
    badge?: string
    disabled?: boolean
  }

  export interface Topic {
    name: string
    topic: string // You can make this more specific, e.g., set it to SETTING_TOPICS enum
    icon: unknown
    settings: Setting[]
    directRoute?: string // Optional: if provided, clicking the topic navigates directly to this route
    externalLink?: string // Optional: if provided, clicking the topic opens this URL in a new tab
    badge?: string
  }

  // Now declare data correctly using these interfaces

  // Base AI settings that are always available
  const baseAISettings = computed(() => [
    {
      name: t('basicSettings'),
      icon: Settings,
      pages: [{ name: t('basicSettings'), page: SETTING_PAGES.AI.BASE_SETTINGS }]
    },
    {
      name: t('aiDocumentTemplates'),
      icon: Copy,
      pages: [
        { name: t('aiDocumentTemplates'), page: SETTING_PAGES.AI.DOCUMENT_GENERATION_TEMPLATES }
      ]
    },
    {
      name: t('promptTemplates'),
      icon: MessageCircleIcon,
      pages: [{ name: t('promptTemplates'), page: SETTING_PAGES.AI.PROMPT_TEMPLATES }]
    }
  ])

  const QuestionnaireSettings = computed(() => [
    {
      name: t('questionnaires'),
      topic: SETTING_TOPICS.QUESTIONNAIRES,
      icon: FileQuestion,
      settings: [],
      directRoute: SETTING_PAGES.QUESTIONNAIRES.QUESTIONNAIRE_LIST
    }
  ])

  const OnboardingPackageSettings = computed(() => [
    {
      name: t('onboardingPackages'),
      topic: SETTING_TOPICS.ONBOARDING_PACKAGES,
      icon: Package,
      settings: [],
      directRoute: SETTING_TOPICS.ONBOARDING_PACKAGES,
      badge: t('new')
    }
  ])

  const ClientOnboardingSettings = computed(() => {
    const settings = [
      {
        name: t('onboardingPackages'),
        icon: Package,
        pages: [{ name: t('onboardingPackages'), page: SETTING_TOPICS.ONBOARDING_PACKAGES }],
        badge: t('new')
      },
      {
        name: t('documentTemplates'),
        icon: FileText,
        pages: [{ name: t('documentTemplates'), page: SETTING_TOPICS.DOCUMENT_TEMPLATES }],
        badge: t('new')
      },
      {
        name: t('questionnaires'),
        icon: FileQuestion,
        pages: [{ name: t('questionnaires'), page: SETTING_TOPICS.QUESTIONNAIRES }]
      }
    ]

    return [
      {
        name: t('clientOnboarding'),
        topic: SETTING_TOPICS.CLIENT_ONBOARDING,
        icon: UserPlus,
        settings: settings
      }
    ]
  })
  // Entity extraction settings that require feature flag
  const entityExtractionSettings = computed(() => [
    {
      name: t('entityExtraction'),
      icon: ScanSearchIcon,
      pages: [
        { name: t('entityExtractionInstructions'), page: SETTING_PAGES.AI.ENTITY_EXTRACTION }
      ],
      beta: true
    }
  ])

  // Call forwarding rules badge logic
  const isPhoneLitePlan = computed(() => {
    return (
      user.value?.org.is_phonebot_self_service_customer &&
      user.value?.org.phonebot_self_service_plan === PhonebotSelfServicePlanEnum.PHONE_BEGINNER_PLAN
    )
  })

  const isInTrialPeriod = computed(() => {
    if (!user.value?.org.phonebot_trial_ends_at) return false

    const trialDateStr = user.value.org.phonebot_trial_ends_at
    let trialEndsAt: Date

    // Check if date is in DD.MM.YYYY format
    if (trialDateStr.includes('.')) {
      const [day, month, year] = trialDateStr.split('.')
      // Month is 0-indexed in JavaScript Date
      trialEndsAt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    } else {
      // Otherwise try standard parsing
      trialEndsAt = new Date(trialDateStr)
    }

    return trialEndsAt > new Date()
  })

  const callForwardingRulesShowTestPhaseBadge = computed(() => {
    return isPhoneLitePlan.value && isInTrialPeriod.value
  })

  const callForwardingRulesShowProBadge = computed(() => {
    return isPhoneLitePlan.value && !isInTrialPeriod.value
  })

  const callForwardingRulesDisabled = computed(() => {
    return callForwardingRulesShowProBadge.value
  })

  // Agentic workflow settings that require feature flag
  // Only show full workflow settings when user is in an impersonated session (Login As)
  const agenticWorkflowSettings = computed(() => {
    const basePages = [
      {
        name: t('basicSettings'),
        page: SETTING_PAGES.AI.CASE_LIFECYCLE_WORKFLOWS_BASIC_SETTINGS
      }
    ]

    // Additional pages only visible during impersonated sessions
    const advancedPages = [
      { name: t('aiWorkflows'), page: SETTING_PAGES.AI.CASE_LIFECYCLE_WORKFLOWS },
      { name: t('aiAutonomy'), page: SETTING_PAGES.AI.AI_AUTONOMY },
      { name: t('notifications'), page: SETTING_PAGES.AI.AI_NOTIFICATIONS }
    ]

    const pages = user.value?.is_impersonated_session ? [...basePages, ...advancedPages] : basePages

    return [
      {
        name: t('aiWorkflows'),
        icon: WorkflowIcon,
        pages
      }
    ]
  })

  const availableTopics = computed<Topic[]>(() => {
    // Build AI settings dynamically based on feature flags and user permissions
    const aiSettings = [...baseAISettings.value]
    const clientOnboardingSettings = []
    const questionnaireSelfServiceSettings = []

    // Add entity extraction settings if either feature flag is enabled
    if (
      preflightDocumentExtractionAgentFlag.isEnabled.value ||
      incomingEmailEntityExtractionAgentFlag.isEnabled.value
    ) {
      aiSettings.push(...entityExtractionSettings.value)
    }

    if (agenticWorkflowsFlag.isEnabled.value && user.value?.org.enable_ai_workflows) {
      aiSettings.push(...agenticWorkflowSettings.value)
    }
    if (clientOnboardingFlag.isEnabled.value) {
      clientOnboardingSettings.push(...ClientOnboardingSettings.value)
    }

    if (questionnaireSelfServiceFlag.isEnabled.value) {
      questionnaireSelfServiceSettings.push(...QuestionnaireSettings.value)
    }

    const topics: Topic[] = [
      {
        name: t('emails'),
        topic: SETTING_TOPICS.EMAILS,
        icon: Mail, // Icons should fit your actual icon system
        settings: [
          {
            name: t('jupusEmailAddress'),
            icon: AtSign,
            pages: [{ name: t('jupusEmailAddress'), page: SETTING_PAGES.EMAILS.JUPUS_EMAIL }]
          },
          {
            name: t('templates'),
            icon: Copy,
            pages: [{ name: t('templates'), page: SETTING_PAGES.EMAILS.TEMPLATES }]
          },
          {
            name: t('design'),
            icon: Palette,
            pages: [{ name: t('design'), page: SETTING_PAGES.EMAILS.DESIGN }]
          },
          {
            name: t('signature'),
            icon: FileSignature,
            pages: [{ name: t('signature'), page: SETTING_PAGES.EMAILS.SIGNATURES }]
          },
          {
            name: t('placeholders'),
            icon: BracketsIcon,
            pages: [{ name: t('placeholders'), page: SETTING_PAGES.EMAILS.PLACEHOLDERS }]
          },
          {
            name: t('notifications'),
            icon: Bell,
            pages: [{ name: t('notifications'), page: SETTING_PAGES.EMAILS.NOTIFICATIONS }]
          },
          {
            name: t('deliveryStatus'),
            icon: CheckCircleIcon,
            pages: [{ name: t('deliveryStatus'), page: SETTING_PAGES.EMAILS.DELIVERY_STATUS }]
          }
        ]
      },
      {
        name: t('users'),
        topic: 'users',
        icon: Users,
        settings: [],
        externalLink: '/users/settings/'
      },
      {
        name: t('lawFirm'),
        topic: 'law-firm',
        icon: Building2,
        settings: [],
        externalLink: '/organisations/settings/'
      },
      {
        name: t('artificialIntelligence'),
        topic: SETTING_TOPICS.AI,
        icon: BotIcon,
        settings: aiSettings
      },
      {
        name: t('aiPhoneAnswering'),
        topic: SETTING_TOPICS.AI_PHONE_ANSWERING,
        icon: Phone,
        settings: [
          {
            name: t('phoneNumbers'),
            icon: Phone,
            pages: [
              { name: t('phoneNumbers'), page: SETTING_PAGES.AI_PHONE_ANSWERING.PHONE_NUMBERS }
            ]
          },
          {
            name: t('callForwardingRules'),
            icon: RefreshCcw,
            pages: [
              {
                name: t('callForwardingRules'),
                page: SETTING_PAGES.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULES
              }
            ],
            badge: callForwardingRulesShowTestPhaseBadge.value
              ? t('testPhase')
              : callForwardingRulesShowProBadge.value
                ? t('pro')
                : undefined,
            disabled: callForwardingRulesDisabled.value
          }
        ]
      },
      {
        name: t('lawOfficeSoftware'),
        topic: SETTING_TOPICS.LAW_OFFICE_SOFTWARE,
        icon: Server,
        settings: [
          {
            name: 'Actaport',
            icon: GlobeIcon,
            pages: [{ name: 'Actaport', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ACTAPORT }]
          },
          {
            name: 'Advolux',
            icon: Monitor,
            pages: [{ name: 'Advolux', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ADVOLUX }]
          },
          {
            name: 'Advoware',
            icon: Monitor,
            pages: [{ name: 'Advoware', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.ADVOWARE }]
          },
          {
            name: 'Jurnodes',
            icon: GlobeIcon,
            pages: [{ name: 'Jurnodes', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.JURNODES }]
          },
          {
            name: 'Kleos',
            icon: GlobeIcon,
            pages: [{ name: 'Kleos', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.KLEOS }]
          },
          {
            name: 'Lawfirm',
            icon: Monitor,
            pages: [{ name: 'Lawfirm', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.LAWFIRM }]
          },
          {
            name: 'RA-MICRO',
            icon: Monitor,
            pages: [{ name: 'RA-MICRO', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.RA_MICRO }]
          },
          {
            name: 'DATEV',
            icon: Monitor,
            pages: [{ name: 'DATEV', page: SETTING_PAGES.LAW_OFFICE_SOFTWARE.DATEV }]
          }
        ].sort((a, b) => a.name.localeCompare(b.name))
      },
      {
        name: t('integrations'),
        topic: SETTING_TOPICS.INTEGRATIONS,
        icon: PlugZap,
        settings: [
          {
            name: t('webhooks'),
            icon: Webhook,
            pages: [{ name: t('webhooks'), page: SETTING_PAGES.INTEGRATIONS.WEBHOOKS }]
          },
          {
            name: t('emailEgress'),
            icon: Mail,
            pages: [{ name: t('emailEgress'), page: SETTING_PAGES.INTEGRATIONS.EMAIL_EGRESS }]
          },
          {
            name: t('hubspot'),
            icon: GlobeIcon,
            pages: [{ name: t('hubspot'), page: SETTING_PAGES.INTEGRATIONS.HUBSPOT }]
          },
          {
            name: t('hibp'),
            icon: ShieldAlert,
            pages: [{ name: t('hibp'), page: SETTING_PAGES.INTEGRATIONS.HIBP }]
          },
          {
            name: t('iusta'),
            icon: Link,
            pages: [{ name: t('iusta'), page: SETTING_PAGES.INTEGRATIONS.IUSTA }]
          }
        ].sort((a, b) => a.name.localeCompare(b.name))
      },
      {
        name: t('appointmentBooking'),
        topic: 'appointment-booking',
        icon: CalendarCheck,
        settings: [],
        externalLink: '/appointments/config/calendars/'
      },
      ...clientOnboardingSettings
    ]

    // Only show email notifications if the org has enabled voice but not cases
    if (user.value?.org.enable_voice && !user.value?.org.enable_cases) {
      const emailTopic = topics.find((topic) => topic.topic === SETTING_TOPICS.EMAILS) as Topic
      if (emailTopic) {
        // Keep only the Notifications setting, remove all others
        emailTopic.settings = emailTopic.settings.filter(
          (setting) => setting.name === t('notifications')
        )
      }
    }

    // Only show Document Templates and Questionnaires as top-level items
    // if Client Onboarding is NOT enabled (they're nested under Client Onboarding otherwise)
    if (!clientOnboardingFlag.isEnabled.value) {
      topics.push({
        name: t('documentTemplates'),
        topic: SETTING_TOPICS.DOCUMENT_TEMPLATES,
        icon: FileText,
        settings: [],
        directRoute: SETTING_PAGES.DOCUMENT_TEMPLATES,
        badge: t('new')
      })
      topics.push({
        name: t('questionnaires'),
        topic: SETTING_TOPICS.QUESTIONNAIRES,
        icon: FileQuestion,
        settings: [],
        directRoute: SETTING_PAGES.QUESTIONNAIRES.QUESTIONNAIRE_LIST
      })
    }

    return topics
  })

  const topics = computed(() => {
    return availableTopics.value.filter((topic) => {
      if (topic.topic == SETTING_TOPICS.AI) {
        return user.value?.org.enable_law_specific_data
      }
      if (topic.topic == SETTING_TOPICS.AI_PHONE_ANSWERING) {
        return user.value?.org.enable_voice
      }
      if (topic.topic == 'appointment-booking') {
        return user.value?.org.enable_appointments
      }
      if (topic.topic == SETTING_TOPICS.INTEGRATIONS) {
        return adminSetIntegrationsFlag.isEnabled.value
      }

      if (
        topic.topic == SETTING_TOPICS.LAW_OFFICE_SOFTWARE ||
        topic.topic == SETTING_TOPICS.DOCUMENT_TEMPLATES ||
        topic.topic == SETTING_TOPICS.QUESTIONNAIRES ||
        topic.topic == SETTING_TOPICS.ONBOARDING_PACKAGES ||
        topic.topic == SETTING_TOPICS.CLIENT_ONBOARDING
      ) {
        return user.value?.org.enable_cases
      }
      return true
    })
  })

  const selectedTopic = ref<Topic | null>(null) // Initially no topic is selected
  const selectedSetting = ref<Setting | null>(null) // Initially no setting is selected
  const selectedPage = ref<Page | null>(null) // Initially no page is selected
  provide('selectedTopic', selectedTopic)
  provide('selectedSetting', selectedSetting)
  provide('selectedPage', selectedPage)

  const handleTopicClick = (topic: Topic) => {
    selectedTopic.value = topic
    if (topic.directRoute) {
      // For topics with direct routes, navigate directly
      selectedSetting.value = null
      selectedPage.value = null
      router.push(`/app/settings/${topic.topic}`)
    } else if (topic.settings.length > 0) {
      // For topics with settings, navigate to first setting's first page
      selectedSetting.value = topic.settings[0]
      selectedPage.value = selectedSetting.value.pages[0]
      router.push(`/app/settings/${topic.topic}/${selectedPage.value.page}`)
    }
  }

  const updateSelections = () => {
    const segments = route.path.split('/').filter((segment) => segment)
    const topicSegment = segments[2]
    // Handle nested routes by joining segments from index 3 onwards
    const pageSegment = segments.length > 3 ? segments.slice(3).join('/') : segments[3] || null

    if (segments.length > 1) {
      selectedTopic.value = topics.value.find((setting) => setting.topic === topicSegment) as Topic
      if (selectedTopic.value && pageSegment) {
        // Check if this topic has a direct route
        if (selectedTopic.value.directRoute && selectedTopic.value.directRoute === pageSegment) {
          // For direct routes, don't set setting or page
          selectedSetting.value = null
          selectedPage.value = null
        } else {
          // For topics with settings, find the matching setting and page
          selectedSetting.value = selectedTopic.value.settings.find((setting) =>
            setting.pages.some((p) => p.page === pageSegment)
          ) as Setting
          if (selectedSetting.value) {
            selectedPage.value = selectedSetting.value.pages.find(
              (p) => p.page === pageSegment
            ) as Page
          }
        }
      }
    }
  }

  watch(route, () => {
    updateSelections()
  })

  onBeforeMount(() => {
    updateSelections()
  })

  onMounted(async () => {
    const fetching = []
    if (!user.value) {
      fetching.push(fetchUser())
    }
    // Check feature flags
    fetching.push(agenticWorkflowsFlag.checkFlag())
    fetching.push(questionnaireSelfServiceFlag.checkFlag())
    fetching.push(preflightDocumentExtractionAgentFlag.checkFlag())
    fetching.push(incomingEmailEntityExtractionAgentFlag.checkFlag())
    fetching.push(pdfFormTemplatesFlag.checkFlag())
    fetching.push(clientOnboardingFlag.checkFlag())
    fetching.push(adminSetIntegrationsFlag.checkFlag())
    await Promise.all(fetching)

    updateSelections()
  })
</script>

<template>
  <SpaCompatComponent v-if="user">
    <div class="">
      <div class="flex">
        <div class="w-1/4 items-center border-e-[1px]">
          <div class="border-b-[1px]">
            <div class="flex h-[70px] items-center justify-between gap-2">
              <h1 class="text-lg font-semibold text-stone-800 dark:text-neutral-200 md:text-xl">
                {{ t('settings') }}
              </h1>
            </div>
          </div>
          <nav class="flex w-full flex-col flex-wrap pb-3 pr-5 pt-5">
            <ul class="space-y-1.5">
              <!-- Topics List -->
              <li
                v-for="topic in topics"
                :key="topic.name"
                class="mb-1.5"
              >
                <!-- External link topics use anchor tags -->
                <a
                  v-if="topic.externalLink"
                  :href="topic.externalLink"
                  class="flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-start text-sm text-gray-800
                    hover:bg-slate-200 focus:outline-none"
                >
                  <component
                    :is="topic.icon"
                    class="size-4 flex-shrink-0"
                  />
                  {{ topic.name }}
                  <ExternalLink class="ms-auto size-3.5 flex-shrink-0 text-gray-400" />
                </a>

                <!-- Internal topics use buttons -->
                <button
                  v-else
                  type="button"
                  :class="selectedTopic?.topic === topic.topic ? 'bg-slate-200' : ''"
                  class="flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-start text-sm text-gray-800
                    hover:bg-slate-200 focus:outline-none"
                  @click="() => handleTopicClick(topic)"
                >
                  <!-- Topic Icon placed here -->
                  <component
                    :is="topic.icon"
                    class="size-4 flex-shrink-0"
                  />

                  {{ topic.name }}

                  <!-- Badge for topic -->
                  <span
                    v-if="topic.badge"
                    class="ml-2 inline-flex items-center justify-center rounded-full bg-[hsl(271,81%,56%)] px-1 py-0.5
                      font-sans text-[10px] font-semibold leading-[12px] text-white"
                  >
                    {{ topic.badge }}
                  </span>

                  <!-- Only show chevrons for topics with settings (no direct route) -->
                  <template v-if="!topic.directRoute && topic.settings.length > 0">
                    <ChevronDown
                      v-if="selectedTopic?.topic === topic.topic"
                      class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                    />
                    <ChevronUp
                      v-else
                      class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                    />
                  </template>
                </button>

                <!-- Settings that belong to the selected topic (only for topics with settings) -->
                <div
                  v-show="
                    selectedTopic?.topic === topic.topic &&
                    !topic.directRoute &&
                    !topic.externalLink &&
                    topic.settings.length > 0
                  "
                  class="transition-[height] duration-300"
                >
                  <ul
                    class="relative mt-1.5 space-y-1.5 ps-7 before:absolute before:start-[18px] before:top-0 before:h-full
                      before:w-0.5 before:bg-slate-200"
                  >
                    <!-- Update this section inside the <li> for settings. -->
                    <li
                      v-for="setting in topic.settings"
                      :key="setting.name"
                      class="flex flex-col gap-y-2"
                    >
                      <button
                        type="button"
                        class="flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-start text-sm text-gray-800
                          hover:bg-slate-200 focus:bg-slate-200 focus:outline-none"
                        :class="[
                          selectedSetting?.name === setting.name ? 'bg-slate-200' : '',
                          setting.disabled
                            ? 'cursor-not-allowed opacity-50 hover:bg-transparent'
                            : ''
                        ]"
                        :disabled="setting.disabled"
                        @click="
                          () => {
                            if (setting.disabled) return
                            selectedSetting = setting
                            selectedPage = setting.pages[0] // default page when you click on the setting
                            router.push(`/app/settings/${topic.topic}/${selectedPage.page}`)
                          }
                        "
                      >
                        <!-- Setting Icon -->
                        <component
                          :is="setting.icon"
                          class="size-4 flex-shrink-0"
                        />
                        {{ setting.name }}

                        <!-- Beta Badge -->
                        <span
                          v-if="setting.beta"
                          class="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800"
                        >
                          BETA
                        </span>

                        <!-- Custom Badge (Testphase, PRO, or NEW) -->
                        <span
                          v-if="setting.badge"
                          :class="
                            setting.badge === t('new')
                              ? `ml-2 inline-flex items-center justify-center rounded-full bg-[hsl(271,81%,56%)] px-1 py-0.5
                                font-sans text-[10px] font-semibold leading-[12px] text-white`
                              : setting.badge === t('testPhase')
                                ? 'ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800'
                                : 'ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800'
                          "
                        >
                          {{ setting.badge }}
                        </span>

                        <!-- Display Chevron if the setting has more than one page -->
                        <ChevronDown
                          v-if="
                            selectedSetting?.name === setting.name &&
                            setting.pages.length > 1 &&
                            !setting.disabled
                          "
                          class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                        />
                        <ChevronUp
                          v-else-if="setting.pages.length > 1 && !setting.disabled"
                          class="ms-auto mt-1 size-3.5 flex-shrink-0 transition"
                        />
                      </button>

                      <!-- Page Links if there are multiple pages in the setting -->
                      <ul
                        v-show="selectedSetting?.name === setting.name && setting.pages.length > 1"
                        class="relative mt-1.5 space-y-1.5 ps-4 before:absolute before:start-[18px] before:top-0 before:h-full
                          before:w-0.5 before:bg-slate-200"
                      >
                        <li
                          v-for="page in setting.pages"
                          :key="page.name"
                          class="ml-4 flex cursor-pointer gap-x-4 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-slate-200
                            focus:bg-slate-200"
                          :class="selectedPage?.name === page.name ? 'bg-slate-200' : ''"
                          @click="
                            () => {
                              selectedPage = page
                              router.push(`/app/settings/${topic.topic}/${page.page}`)
                            }
                          "
                        >
                          <button type="button">
                            {{ page.name }}
                          </button>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </nav>
        </div>
        <div class="w-3/4">
          <router-view></router-view>
        </div>
      </div>
    </div>
  </SpaCompatComponent>
</template>

<style scoped>
  /* Scoped styling if needed */
</style>
